import { X } from 'lucide-react';
import { hasStructuredOncology, CANCER_TYPES, useTreatmentReferentials } from '@/components/shared/OncologyContext';

/**
 * Modal d'édition des informations oncologiques du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */
export default function EditPatientModal({
  isOpen,
  onClose,
  patient,
  editForm,
  setEditForm,
  onConfirm,
  loading,
}) {
  const { treatmentTypes, therapeuticLines } = useTreatmentReferentials();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Contexte oncologique</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Legacy info if present */}
          {(patient.pathology || patient.treatment) && !hasStructuredOncology(patient) && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <p className="font-medium mb-1">Anciennes données :</p>
              <p>Pathologie: {patient.pathology || "—"}</p>
              <p>Traitement: {patient.treatment || "—"}</p>
            </div>
          )}
          
          {/* Cancer Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type de cancer</label>
            <select
              value={editForm.cancerType}
              onChange={e => setEditForm(f => ({ ...f, cancerType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            >
              <option value="">Sélectionner...</option>
              {CANCER_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          {/* Cancer Type Detail */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Détail type de cancer</label>
            <input
              type="text"
              value={editForm.cancerTypeDetail}
              onChange={e => setEditForm(f => ({ ...f, cancerTypeDetail: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
              placeholder="Ex: HER2+, triple négatif..."
            />
          </div>
          
          {/* Treatment Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type de traitement</label>
            <select
              value={editForm.treatmentType}
              onChange={e => setEditForm(f => ({ ...f, treatmentType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            >
              <option value="">Sélectionner...</option>
              {treatmentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          {/* Therapeutic Line */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Ligne thérapeutique</label>
            <select
              value={editForm.therapeuticLine}
              onChange={e => setEditForm(f => ({ ...f, therapeuticLine: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            >
              <option value="">Sélectionner...</option>
              {therapeuticLines.map(line => (
                <option key={line} value={line}>{line}</option>
              ))}
            </select>
          </div>
          
          {/* Protocol */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Protocole</label>
            <input
              type="text"
              value={editForm.protocol}
              onChange={e => setEditForm(f => ({ ...f, protocol: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
              placeholder="Ex: FOLFOX, KEYTRUDA, NIVOLUMAB..."
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? "..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
